import { useEffect } from "react";

export const SITE_URL = "https://delawareps.com";
const BRAND = "InfoRetrieval";

export function breadcrumbJsonLd(items) {
  return { "@type": "BreadcrumbList", itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}` })) };
}

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

function setJsonLd(data) {
  document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-seo-jsonld", "1");
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
}

export function useSeo({ title, description, path = "/", jsonLd = null, noindex = false, image = "/og-image.png" } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${BRAND}` : `Delaware Process Server | Licensed Investigators Since 1997 | ${BRAND}`;
    const url = `${SITE_URL}${path}`;
    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", noindex ? null : url);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", BRAND);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", `${SITE_URL}${image}`);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", `${SITE_URL}${image}`);
    setJsonLd(jsonLd);
  }, [title, description, path, noindex, image, JSON.stringify(jsonLd)]);
}

export function businessJsonLd(site) {
  const org = site?.org || {};
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    name: org.name || BRAND,
    url: SITE_URL,
    image: `${SITE_URL}/og-image.png`,
    logo: `${SITE_URL}/icon-512.png`,
    description: "Woman-owned, family-run Delaware process servers and licensed investigators since 1997. Service of process, asset locates, skip tracing and direct record retrieval across New Castle, Kent and Sussex Counties and the Eastern Shore.",
    telephone: org.phone || undefined,
    email: org.email || "ProcessServer@yourdelawarepi.com",
    address: org.address ? { "@type": "PostalAddress", streetAddress: org.address, addressRegion: "DE", addressCountry: "US" } : { "@type": "PostalAddress", addressRegion: "DE", addressCountry: "US" },
    foundingDate: "1997",
    priceRange: "$$",
    areaServed: ["New Castle County, DE", "Kent County, DE", "Sussex County, DE", "Eastern Shore, MD", "Delaware", "Maryland"].map((n) => ({ "@type": "AdministrativeArea", name: n })),
    memberOf: { "@type": "Organization", name: "National Association of Professional Process Servers (NAPPS)" },
    knowsAbout: ["Service of process", "Skip tracing", "Asset searches", "Record retrieval", "Delaware Court of Chancery", "Delaware Superior Court"],
    hasOfferCatalog: {
      "@type": "OfferCatalog", name: "Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Process Service", url: `${SITE_URL}/process-service` } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Asset Locates & Skip Tracing", url: `${SITE_URL}/investigations` } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Record Retrieval", url: `${SITE_URL}/records` } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Criminal Background Screening & Court Research", url: `${SITE_URL}/background-screening` } },
      ],
    },
    sameAs: ["https://napps.org"],
  };
}

export function serviceJsonLd({ name, description, path, serviceType }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name, description, serviceType,
    url: `${SITE_URL}${path}`,
    provider: { "@id": `${SITE_URL}/#business`, "@type": "LocalBusiness", name: BRAND, url: SITE_URL },
    areaServed: ["Delaware", "Eastern Shore, Maryland"].map((n) => ({ "@type": "AdministrativeArea", name: n })),
  };
}

export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  };
}
